import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Alert, Button, Group, PinInput, Stack, Text, TextInput } from "@mantine/core";

import { CenteredCard } from "@/components/CenteredCard";
import { emailOtp } from "@/lib/authClient";
import { authSearchSchema, safeNextUrl } from "@/features/auth/redirect";

/**
 * Email verification by 6-digit code.
 *
 * The backend routes every verification path through OTP rather than a link
 * (`overrideDefaultEmailVerification: true`), which is what makes the invitation
 * flow survivable: a magic link would open a new tab and abandon the pending
 * invitation, whereas a code is typed in place.
 *
 * On success the API issues a session immediately
 * (`autoSignInAfterVerification: true`), so there is no separate sign-in step
 * before returning to the invitation.
 */
function VerifyEmailPage() {
  const { email: emailFromSearch, next } = Route.useSearch();

  const [email, setEmail] = useState(emailFromSearch ?? "");
  const [otp, setOtp] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const verifyMutation = useMutation({
    mutationFn: async () => {
      const result = await emailOtp.verifyEmail({ email, otp });
      if (result.error)
        throw new Error(
          result.error.message ?? "That code didn't work. Try again or send a new one.",
        );
      window.location.assign(safeNextUrl(next));
    },
  });
  const resendMutation = useMutation({
    mutationFn: async () => {
      const result = await emailOtp.sendVerificationOtp({ email, type: "email-verification" });
      if (result.error)
        throw new Error(result.error.message ?? "Could not send a code. Try again shortly.");
      setNotice("We've sent a new code. It expires in 10 minutes.");
    },
  });
  const verify = () => {
    setNotice(null);
    resendMutation.reset();
    verifyMutation.mutate();
  };
  const resend = () => {
    setNotice(null);
    verifyMutation.reset();
    resendMutation.mutate();
  };
  const verifying = verifyMutation.isPending;
  const resending = resendMutation.isPending;
  const error = verifyMutation.error?.message ?? resendMutation.error?.message;

  const canSubmit = email.trim().length > 0 && otp.length === 6;

  return (
    <CenteredCard
      title="Check your email"
      description={
        email
          ? `Enter the 6-digit code we sent to ${email}. It expires in 10 minutes.`
          : "Enter your email address and the 6-digit code we sent you."
      }
    >
      <Stack gap="md">
        {!emailFromSearch ? (
          <TextInput
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
        ) : null}

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Verification code
          </Text>
          <PinInput
            length={6}
            type="number"
            oneTimeCode
            aria-label="Verification code"
            value={otp}
            onChange={setOtp}
          />
        </Stack>

        {error ? (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        ) : null}

        {notice ? (
          <Alert color="green" variant="light">
            {notice}
          </Alert>
        ) : null}

        <Group grow>
          <Button
            size="md"
            disabled={!canSubmit || resending}
            loading={verifying}
            onClick={() => void verify()}
          >
            Verify
          </Button>
          <Button
            size="md"
            variant="default"
            disabled={email.trim().length === 0 || verifying}
            loading={resending}
            onClick={() => void resend()}
          >
            Resend code
          </Button>
        </Group>
      </Stack>
    </CenteredCard>
  );
}

export const Route = createFileRoute("/auth/verify-email")({
  validateSearch: authSearchSchema,
  component: VerifyEmailPage,
});
